import supabase from '@/api/supabase';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'react-hot-toast';

// Types
export interface ServiceAvatar {
  shape: string;
  color1: string;
  color2: string;
}

export interface Settings {
  id: number;
  companyName?: string;
  companyLogo?: string;
  timeZone?: string;
  timeReset?: string;
  employeeRole?: string[];
  specialCategory?: string[];
  maxListHeight?: number;
  serviceCategory?: string[];
  serviceAvatar?: ServiceAvatar[];
  themeGrid?: boolean;
}

// API Constants
const SETTINGS_QUERY_KEY = ['settings'] as const;
const SETTINGS_TABLE = 'settings';

/**
 * API Functions for Settings Management
 */

async function getSettings(): Promise<Settings> {
  const { data, error } = await supabase.from(SETTINGS_TABLE).select('*').single();

  if (error) throw new Error(error.message);
  return data;
}

async function updateSettings(settings: Partial<Settings>): Promise<Settings> {
  const { data, error } = await supabase
    .from(SETTINGS_TABLE)
    .update(settings)
    .eq('id', settings.id)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Subscribe to settings changes in real-time
 */
function subscribeToSettings(callback: () => void) {
  const subscription = supabase
    .channel('settings_changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: SETTINGS_TABLE,
      },
      callback
    )
    .subscribe();

  return () => {
    subscription.unsubscribe();
  };
}

/**
 * Custom hook for managing application settings
 * Provides real-time updates, CRUD operations, and loading states
 * @returns {Object} Settings data and mutation handlers
 */
export function useSettings() {
  const queryClient = useQueryClient();

  // Set up real-time updates subscription
  useEffect(() => {
    const unsubscribe = subscribeToSettings(() => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
    });

    return () => unsubscribe();
  }, [queryClient]);

  // Fetch settings data
  const {
    data: settings,
    isLoading,
    error,
  } = useQuery<Settings>({
    queryKey: SETTINGS_QUERY_KEY,
    queryFn: getSettings,
  });

  // Handle settings updates
  const updateSettingsMutation = useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
      toast.success('Settings updated successfully!');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update settings: ${error.message}`);
    },
  });

  return {
    settings,
    isLoading,
    error,
    updateSettings: updateSettingsMutation.mutate,
    isUpdating: updateSettingsMutation.isPending,
  } as const;
}
